import * as Joi from 'joi';
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	OneToMany,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Base from '../base.entity';
import ChangeScene from '../change-scenes/change-scene.entity';
import Change from '../changes/change.entity';
import CharScene from '../char-scenes/char-scene.entity';
import Character from '../characters/character.entity';
import Episodic from '../episodics/episodic.entity';
import HaChangeScene from '../ha-change-scenes/ha-change-scene.entity';
import HaChange from '../ha-changes/ha-change.entity';
import MuChangeScene from '../mu-change-scenes/mu-change-scene.entity';
import MuChange from '../mu-changes/mu-change.entity';
import ProdPermission from '../prod-permissions/prod-permission.entity';
import PropScene from '../prop-scenes/prop-scene.entity';
import Scene from '../scenes/scene.entity';
import SetProd from '../set-prods/set-prod.entity';

@Entity('productions')
export default class Production extends Base {
	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	episodic_id: number;

	@Column('varchar')
	prod_name: string;

	@Column('varchar')
	episode_num: string;

	@Column({ type: 'int', transformer: booleanTransformer })
	is_active: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	archived: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	excluded: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('text')
	notes: string;

	@Column('int')
	payment_id: number;

	@Column({ type: 'datetime', transformer: momentTransformer })
	last_activity: Moment;

	@ManyToOne((type) => Episodic, (episodic) => episodic.productions)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	@OneToMany((type) => Scene, (scene) => scene.production)
	scenes: Scene[];

	@OneToMany((type) => Character, (character) => character.production)
	characters: Character[];

	@OneToMany(
		(type) => ProdPermission,
		(prodPermission) => prodPermission.production
	)
	prodPermissions: ProdPermission[];

	@OneToMany((type) => SetProd, (setProd) => setProd.production)
	setProds: SetProd[];

	@OneToMany((type) => Change, (change) => change.production)
	changes: Change[];

	@OneToMany((type) => HaChange, (haChange) => haChange.production)
	haChanges: HaChange[];

	@OneToMany((type) => MuChange, (muChange) => muChange.production)
	muChanges: MuChange[];

	@OneToMany((type) => PropScene, (propScene) => propScene.production)
	propScenes: PropScene[];

	@OneToMany((type) => ChangeScene, (changeScene) => changeScene.production)
	changeScenes: ChangeScene[];

	@OneToMany(
		(type) => HaChangeScene,
		(haChangeScene) => haChangeScene.production
	)
	haChangeScenes: HaChangeScene[];

	@OneToMany(
		(type) => MuChangeScene,
		(muChangeScene) => muChangeScene.production
	)
	muChangeScenes: MuChangeScene[];

	@OneToMany((type) => CharScene, (charScene) => charScene.production)
	charScenes: CharScene[];

	static rules() {
		return {
			id: Joi.number().integer(),
			eidr_id: Joi.string().max(40).allow(null),
			episodic_id: Joi.number().integer().allow(null),
			prod_name: Joi.string().max(140).allow(null),
			episode_num: Joi.string().max(30).allow(null),
			is_active: Joi.boolean().allow(null),
			archived: Joi.boolean().allow(null),
			excluded: Joi.boolean(),
			date_created: Joi.date().allow(null),
			created_by: Joi.number().integer(),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			notes: Joi.string().max(1000).allow(null, ''),
			payment_id: Joi.number().integer(),
			last_activity: Joi.date(),
		};
	}

	toJSON() {
		return {
			id: this.id,
			episodic_id: this.episodic_id,
			prod_name: this.prod_name,
			episode_num: this.episode_num,
			is_active: this.is_active,
			archived: this.archived,
			excluded: this.excluded,
			date_created: this.date_created,
			created_by: this.created_by,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			notes: this.notes,
			payment_id: this.payment_id,
			last_activity: this.last_activity,
		};
	}

	assign(props?: GenPartial<Production>) {
		super.assign(props);

		if (props) {
			if (typeof props.is_active !== 'undefined') {
				this.is_active = booleanTransformer.from(props.is_active);
			}
			if (typeof props.archived !== 'undefined') {
				this.archived = booleanTransformer.from(props.archived);
			}
			if (typeof props.excluded !== 'undefined') {
				this.excluded = booleanTransformer.from(props.excluded);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.last_activity !== 'undefined') {
				this.last_activity = momentTransformer.from(props.last_activity);
			}
		}
	}

	constructor(props?: GenPartial<Production>) {
		super(props);
	}

	getEpisodeNum(): string {
		if (this.episode_num) {
			return this.episode_num;
		} else {
			return this.prod_name;
		}
	}

	getEpisodeNumWithProdName(hyphen: boolean = true): string {
		let result = '';
		if (this.episode_num) {
			result += this.episode_num;
			result += hyphen ? ' - ' : ' ';
		}
		result += this.prod_name;
		return result;
	}

	getEpName(hyphen: boolean = true): string {
		let prodName = this.prod_name;
		if (this.episodic.is_feature) {
			prodName = this.episodic.isLikeAFeature()
				? `Season ${prodName}`
				: prodName;
		} else {
			prodName = this.getEpisodeNumWithProdName(hyphen);
		}
		return prodName;
	}
}
